/**
 * Recover from Vite CSS / dynamic-import preload failures.
 *
 * Lazy vue-router chunks preload companion CSS. When that fails (deploy /
 * CloudFront race, flaky mobile network, Safari link.onerror quirks), the
 * dynamic import rejects and navigation aborts — leaving customers mid-flow
 * (e.g. PaymentPage). Vite documents listening for `vite:preloadError` and
 * reloading; we also catch the same failures via `router.onError`.
 *
 * Reloads at most once per tab session (sessionStorage survives the reload)
 * to avoid infinite loops when an asset is genuinely missing. Cleared after
 * a successful vue-router navigation so a later deploy can recover again.
 *
 * @see https://vite.dev/guide/build.html#load-error-handling
 * @see MOVEPRO-BOOK-AE / MOVEPRO-BOOK-7P
 */

export const PRELOAD_RELOAD_FLAG = 'mp:vite-preload-reloaded';

/**
 * Failures raised while preloading a route's companion CSS.
 *
 * Vite only ever emits `Unable to preload CSS for ${dep}`. `Loading CSS chunk
 * N failed` is a webpack-era message it never produces; it is retained only so
 * `isVitePreloadFailure` keeps matching what it matched before this split.
 */
const CSS_PRELOAD_FAILURE_PATTERNS = [/Unable to preload CSS/i, /Loading CSS chunk .* failed/i];

/** Failures raised while fetching the route's own JS module. */
const MODULE_PRELOAD_FAILURE_PATTERNS = [
    /Failed to fetch dynamically imported module/i,
    /error loading dynamically imported module/i,
    /Importing a module script failed/i,
];

const PRELOAD_FAILURE_PATTERNS = [...CSS_PRELOAD_FAILURE_PATTERNS, ...MODULE_PRELOAD_FAILURE_PATTERNS];

function sessionGet(key: string): string | null {
    try {
        return sessionStorage.getItem(key);
    } catch {
        return null;
    }
}

function sessionSet(key: string, value: string): void {
    try {
        sessionStorage.setItem(key, value);
    } catch {
        // ignore — private mode / denied storage
    }
}

function sessionRemove(key: string): void {
    try {
        sessionStorage.removeItem(key);
    } catch {
        // ignore
    }
}

function errorMessage(error: unknown): string {
    return (
        typeof error === 'string' ? error
        : error instanceof Error ? error.message
        : error && typeof error === 'object' && 'message' in error ? String((error as { message: unknown }).message)
        : ''
    );
}

/** True when the error looks like a Vite / chunk preload failure. */
export function isVitePreloadFailure(error: unknown): boolean {
    const message = errorMessage(error);

    if (!message) {
        return false;
    }

    return PRELOAD_FAILURE_PATTERNS.some((pattern) => pattern.test(message));
}

/**
 * True only for a CSS preload failure. Vite still imports the module after one
 * of these, so cancelling its rethrow is safe. Cancelling a module fetch
 * failure is not: the dynamic import then resolves with `undefined`.
 */
export function isCssPreloadFailure(error: unknown): boolean {
    const message = errorMessage(error);

    if (!message) {
        return false;
    }

    return CSS_PRELOAD_FAILURE_PATTERNS.some((pattern) => pattern.test(message));
}

/**
 * Backs the sessionStorage guard for this page load. Where site data is denied
 * every sessionStorage call is swallowed, and the guard would otherwise let
 * every failure claim a reload.
 */
let claimedThisPageLoad = false;

/**
 * Returns true the first time a reload is allowed for this tab session.
 * Subsequent calls return false until the flag is cleared.
 */
export function claimPreloadReload(): boolean {
    if (claimedThisPageLoad || sessionGet(PRELOAD_RELOAD_FLAG) === '1') {
        return false;
    }

    claimedThisPageLoad = true;
    sessionSet(PRELOAD_RELOAD_FLAG, '1');
    return true;
}

/** Clear the once-per-session guard after a successful navigation. */
export function clearPreloadReloadFlag(): void {
    claimedThisPageLoad = false;
    sessionRemove(PRELOAD_RELOAD_FLAG);
}

export type PreloadReloadDeps = {
    reload?: () => void;
    claim?: () => boolean;
};

/**
 * If `error` is a preload/chunk failure and we have not already reloaded this
 * session, claim the reload slot and hard-refresh. Returns whether a reload
 * was triggered.
 */
export function reloadOnVitePreloadFailure(error: unknown, deps: PreloadReloadDeps = {}): boolean {
    if (!isVitePreloadFailure(error)) {
        return false;
    }

    const claim = deps.claim ?? claimPreloadReload;
    if (!claim()) {
        return false;
    }

    const reload = deps.reload ?? (() => window.location.reload());
    reload();
    return true;
}

/**
 * Register the Vite `vite:preloadError` listener. Call once at app boot.
 *
 * Only a CSS preload failure is cancelled. Vite's helper ends with
 * `baseModule().catch(handlePreloadError)`, and `handlePreloadError` rethrows
 * only while the event is not `defaultPrevented`. Cancelling a module fetch
 * failure therefore resolves the dynamic import with `undefined`, which
 * vue-router reports as `Couldn't resolve component "default"`
 * (MOVEPRO-CRM-NJ). A reload is still claimed for both.
 *
 * The cancel is decided before the reload claim, so a repeat CSS failure is
 * still cancelled once the single reload has been consumed.
 */
export function registerVitePreloadRecovery(
    target: Pick<Window, 'addEventListener'> = window,
    deps: PreloadReloadDeps = {},
): void {
    const claim = deps.claim ?? claimPreloadReload;
    const reload = deps.reload ?? (() => window.location.reload());

    target.addEventListener('vite:preloadError', ((event: Event) => {
        // Vite only dispatches this for failed CSS/module preloads.
        //
        // Cancel before the reload claim, not after. Whether to cancel is a
        // property of the error; whether to reload is a property of the
        // session. Vite dispatches one event per failed CSS dep, so gating the
        // cancel behind the claim let a second CSS failure rethrow and abort a
        // navigation that would otherwise have rendered.
        if (isCssPreloadFailure((event as Event & { payload?: unknown }).payload)) {
            event.preventDefault();
        }

        if (!claim()) {
            return;
        }

        reload();
    }) as EventListener);
}
